import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../../services/api';
import { Table } from '../../components/common/Table';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import { PageHeader } from '../../components/layout/PageHeader';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, EyeIcon, FolderOpenIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const EMPTY = { name:'', description:'', status:'planning', priority:'medium', startDate:'', endDate:'', budget:'', client:'', stage: 'initiation' };

const PROJECT_STAGES = [
  { key:'initiation',   label:'Initiation',   color:'bg-gray-400' },
  { key:'planning',     label:'Planning',     color:'bg-blue-400' },
  { key:'design',       label:'Design',       color:'bg-purple-400' },
  { key:'execution',    label:'Execution',    color:'bg-yellow-400' },
  { key:'testing',      label:'Testing',      color:'bg-orange-400' },
  { key:'handover',     label:'Handover',     color:'bg-indigo-400' },
  { key:'completed',    label:'Completed',    color:'bg-green-500' },
];

function StageTracker({ stage = 'initiation' }) {
  const currentIdx = PROJECT_STAGES.findIndex(s => s.key === stage);
  return (
    <div className="flex items-center gap-0.5">
      {PROJECT_STAGES.map((s, i) => {
        const done    = i < currentIdx;
        const current = i === currentIdx;
        return (
          <div key={s.key} title={s.label} className={`h-1.5 flex-1 rounded-full transition-all ${
            done    ? 'bg-indigo-500' :
            current ? s.color :
            'bg-gray-200'
          }`} />
        );
      })}
      <span className="ml-2 text-[10px] font-semibold text-gray-500 whitespace-nowrap capitalize">
        {PROJECT_STAGES.find(s => s.key === stage)?.label || stage}
      </span>
    </div>
  );
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setPro]   = useState([]);
  const [pg,       setPg]    = useState(null);
  const [loading,  setL]     = useState(true);
  const [search,   setSrch]  = useState('');
  const [status,   setSt]    = useState('');
  const [page,     setPage]  = useState(1);
  const [open,     setOpen]  = useState(false);
  const [editing,  setEd]    = useState(null);
  const [form,     setForm]  = useState(EMPTY);
  const [saving,   setSv]    = useState(false);

  const load = useCallback(async () => {
    setL(true);
    try {
      const r = await projectsAPI.getAll({ search, status, page, limit: 12 });
      setPro(r.data || []); setPg(r.pagination);
    } catch { toast.error('Failed to load'); }
    finally { setL(false); }
  }, [search, status, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEd(null); setForm(EMPTY); setOpen(true); };
  const openEdit   = p  => {
    setEd(p);
    setForm({
      name: p.name, description: p.description || '', status: p.status, priority: p.priority,
      startDate: p.startDate ? format(new Date(p.startDate), 'yyyy-MM-dd') : '',
      endDate:   p.endDate   ? format(new Date(p.endDate),   'yyyy-MM-dd') : '',
      budget: p.budget || '', client: p.client || '',
      stage: p.stage || 'initiation',
    });
    setOpen(true);
  };

  const del = async id => {
    if (!window.confirm('Delete project?')) return;
    try { await projectsAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const save = async e => {
    e.preventDefault(); setSv(true);
    try {
      editing ? await projectsAPI.update(editing._id, form) : await projectsAPI.create(form);
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false); load();
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSv(false); }
  };

  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const cols = [
    { header: 'Project', render: p => (
      <div>
        <button onClick={() => navigate('/admin/projects/' + p._id)}
          className="font-semibold text-sm text-indigo-600 hover:text-indigo-800 hover:underline text-left">
          {p.name}
        </button>
        <p className="text-xs text-gray-400">{p.client || 'No client'}</p>
      </div>
    )},
    { header: 'Status',   render: p => <Badge status={p.status} /> },
    { header: 'Priority', render: p => <Badge status={p.priority} /> },
    { header: 'Stage', render: p => (
      p.status === 'active' || p.status === 'planning' ? (
        <div className="w-40"><StageTracker stage={p.stage || 'initiation'} /></div>
      ) : <span className="text-xs text-gray-400">—</span>
    )},
    { header: 'Progress', render: p => (
      <div className="w-24">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-400">{p.progress || 0}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: (p.progress || 0) + '%' }} />
        </div>
      </div>
    )},
    { header: 'Budget', render: p => (
      <span className="text-sm font-medium text-gray-700">
        {'₹' + Number(p.budget || 0).toLocaleString('en-IN')}
      </span>
    )},
    { header: 'Manager', render: p => <span className="text-sm text-gray-600">{p.manager?.name || '—'}</span> },
    { header: '', render: p => (
      <div className="flex gap-1">
        <button onClick={() => navigate('/admin/projects/' + p._id)}
          title="Open Project" className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors">
          <FolderOpenIcon className="w-4 h-4 text-indigo-600" />
        </button>
        <button onClick={() => openEdit(p)} title="Edit"
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
          <PencilIcon className="w-4 h-4 text-gray-500" />
        </button>
        <button onClick={() => del(p._id)} title="Delete"
          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors">
          <TrashIcon className="w-4 h-4 text-red-500" />
        </button>
      </div>
    )},
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Projects"
        subtitle="Click any project to view materials, sub-contractors & P&L"
        actions={<Button icon={PlusIcon} onClick={openCreate}>New Project</Button>}
      />

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-44">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => { setSrch(e.target.value); setPage(1); }}
              placeholder="Search projects..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <select value={status} onChange={e => { setSt(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Status</option>
            {['planning','active','on-hold','completed','cancelled'].map(s =>
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            )}
          </select>
        </div>

        <Table columns={cols} data={projects} loading={loading} />
        <Pagination pagination={pg} onPageChange={setPage} />
      </div>

      {/* Tip banner */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center gap-3">
        <FolderOpenIcon className="w-5 h-5 text-indigo-500 flex-shrink-0" />
        <p className="text-sm text-indigo-700">
          <strong>Tip:</strong> Click the project name or the folder icon to open the full project detail —
          add material invoices, sub-contractor invoices, and view the P&L statement.
        </p>
      </div>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={editing ? 'Edit Project' : 'New Project'} size="lg">
        <form onSubmit={save} className="space-y-4">
          <FormField label="Name" required>
            <Input value={form.name} onChange={e => s('name', e.target.value)} required placeholder="Project Alpha" />
          </FormField>
          <FormField label="Description">
            <Textarea value={form.description} onChange={e => s('description', e.target.value)} placeholder="Brief overview..." rows={2} />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status">
              <Select value={form.status} onChange={e => s('status', e.target.value)}>
                {['planning','active','on-hold','completed','cancelled'].map(v =>
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                )}
              </Select>
            </FormField>
            <FormField label="Priority">
              <Select value={form.priority} onChange={e => s('priority', e.target.value)}>
                {['low','medium','high','critical'].map(v =>
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                )}
              </Select>
            </FormField>
          </div>
          <FormField label="Project Stage" hint="Track which phase this project is currently in">
            <div className="grid grid-cols-4 gap-2">
              {PROJECT_STAGES.map(st => (
                <button key={st.key} type="button" onClick={() => s('stage', st.key)}
                  className={`py-2 px-2 rounded-xl text-xs font-semibold border-2 transition-all text-center ${
                    form.stage === st.key
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}>
                  {st.label}
                </button>
              ))}
            </div>
            {form.stage && (
              <div className="mt-2"><StageTracker stage={form.stage} /></div>
            )}
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start Date">
              <Input type="date" value={form.startDate} onChange={e => s('startDate', e.target.value)} />
            </FormField>
            <FormField label="End Date">
              <Input type="date" value={form.endDate} onChange={e => s('endDate', e.target.value)} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Budget / Contract Value (₹)" hint="Used for P&L calculation">
              <Input type="number" value={form.budget} onChange={e => s('budget', e.target.value)} placeholder="0" />
            </FormField>
            <FormField label="Client Name">
              <Input value={form.client} onChange={e => s('client', e.target.value)} placeholder="Client or company name" />
            </FormField>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={saving} className="flex-1">{editing ? 'Update' : 'Create'} Project</Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}